import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  IconButton,
  Tooltip,
  CardActions,
  Divider,
  useMediaQuery,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  ViewModule as CardViewIcon,
  ViewList as TableViewIcon,
} from '@mui/icons-material';

import { deviceAPI, simulationAPI, MonitorWebSocket } from '../services/api';

const Dashboard = ({ showNotification }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [devices, setDevices] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [ws, setWs] = useState(null);
  const [viewMode, setViewMode] = useState(isMobile ? 'card' : 'table'); // Default: cards on mobile, table on desktop

  // Auto-switch to card view on mobile screens
  useEffect(() => {
    setViewMode(isMobile ? 'card' : 'table');
  }, [isMobile]);

  useEffect(() => {
    loadData();

    // Setup WebSocket
    const websocket = new MonitorWebSocket(
      handleWSMessage,
      handleWSError,
      handleWSClose
    );
    websocket.connect();
    setWs(websocket);

    // Cleanup
    return () => {
      if (websocket) {
        websocket.disconnect();
      }
    };
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [devicesData, statusesData] = await Promise.all([
        deviceAPI.getAll(),
        simulationAPI.getAllStatuses(),
      ]);
      setDevices(devicesData);
      setStatuses(statusesData);
    } catch (error) {
      showNotification('Failed to load data', 'error');
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWSMessage = (data) => {
    console.log('WebSocket message:', data);
    setWsConnected(true);
    
    if (data.type === 'status_update' || data.type === 'initial_status') {
      setStatuses(data.data || []);
    }
  };

  const handleWSError = (error) => {
    console.error('WebSocket error:', error);
    setWsConnected(false);
  };

  const handleWSClose = () => {
    console.log('WebSocket closed');
    setWsConnected(false);
  };

  const handleStartSimulation = async (deviceId) => {
    try {
      const result = await simulationAPI.start(deviceId);
      showNotification(
        result?.already_running ? 'Simulation already running' : 'Simulation started',
        'success'
      );
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to start simulation';
      showNotification(detail, 'error');
      console.error('Start error:', error);
    }
  };

  const handleStopSimulation = async (deviceId) => {
    try {
      await simulationAPI.stop(deviceId);
      showNotification('Simulation stopped', 'success');
      loadData();
    } catch (error) {
      showNotification('Failed to stop simulation', 'error');
      console.error('Stop error:', error);
    }
  };

  const getDeviceStatus = (deviceId) => {
    return statuses.find((s) => s.device_id === deviceId) || {};
  };

  const formatUptime = (seconds) => {
    if (!seconds) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const getTotalPacketsSent = () => {
    return statuses.reduce((sum, s) => sum + (s.packets_sent || 0), 0);
  };

  const getRunningCount = () => {
    return statuses.filter((s) => s.status === 'running').length;
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box>
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }
      }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            icon={wsConnected ? <ActiveIcon /> : <InactiveIcon />}
            label={wsConnected ? 'Live Updates' : 'Disconnected'}
            color={wsConnected ? 'success' : 'default'}
            size="small"
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
            aria-label="view mode"
          >
            <ToggleButton value="card" aria-label="card view">
              <Tooltip title="Card View">
                <CardViewIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <Tooltip title="Table View">
                <TableViewIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Devices
              </Typography>
              <Typography variant="h3">
                {devices.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Running Simulations
              </Typography>
              <Typography variant="h3" color="primary">
                {getRunningCount()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Packets Sent
              </Typography>
              <Typography variant="h3" color="secondary">
                {getTotalPacketsSent().toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                WebSocket Status
              </Typography>
              <Typography variant="h3">
                {wsConnected ? '✓' : '✗'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Devices List - Card or Table View */}
      {viewMode === 'card' ? (
        /* Card View */
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Device Status
          </Typography>
          {devices.length === 0 ? (
            <Card>
              <CardContent>
                <Typography color="textSecondary" align="center">
                  No devices found. Go to Devices tab to create one.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={2}>
              {devices.map((device) => {
                const status = getDeviceStatus(device.id);
                const isRunning = status.status === 'running';
                
                // Format ECG profile name for display
                const formatECGProfile = (profile) => {
                  if (!profile) return 'Normal';
                  const profileLabels = {
                    'normal': 'Normal Sinus',
                    'real_dgt7_normal': 'Real DGT7 Normal',
                    'neonate': 'Neonate',
                    'tachycardia': 'Tachycardia',
                    'bradycardia': 'Bradycardia',
                    'afib': 'AFib',
                    'ventricular_tachycardia': 'V-Tach',
                    'ventricular_fibrillation': 'V-Fib',
                    'atrial_flutter': 'A-Flutter',
                    'first_degree_heart_block': '1° Block',
                    'second_degree_heart_block': '2° Block',
                    'third_degree_heart_block': '3° Block',
                    'stemi': 'STEMI',
                    'stroke': 'Stroke'
                  };
                  return profileLabels[profile] || profile;
                };
                
                // Get profile color
                const getProfileColor = (profile) => {
                  const criticalProfiles = ['stemi', 'stroke', 'ventricular_tachycardia', 'ventricular_fibrillation', 'third_degree_heart_block'];
                  const warningProfiles = ['tachycardia', 'bradycardia', 'afib', 'second_degree_heart_block'];
                  
                  if (criticalProfiles.includes(profile)) return 'error';
                  if (warningProfiles.includes(profile)) return 'warning';
                  return 'default';
                };
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={device.id}>
                    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" component="div" gutterBottom>
                              {device.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace' }}>
                              {device.serial_number}
                            </Typography>
                          </Box>
                          <Chip
                            label={status.status || device.status}
                            color={isRunning ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>
                        
                        <Divider sx={{ my: 1.5 }} />
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              ECG Profile:
                            </Typography>
                            <Chip
                              label={formatECGProfile(device.ecg_profile)}
                              color={getProfileColor(device.ecg_profile)}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                          
                          {isRunning && (
                            <>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                  Uptime:
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {formatUptime(status.uptime_seconds)}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                  Packets Sent:
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {(status.packets_sent || 0).toLocaleString()}
                                </Typography>
                              </Box>
                              
                              {status.current_vitals && (
                                <>
                                  <Divider sx={{ my: 0.5 }} />
                                  <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                    Current Vitals:
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip label={`HR: ${status.current_vitals.hr || 'N/A'} bpm`} size="small" variant="outlined" />
                                    <Chip label={`SpO2: ${status.current_vitals.spo2 || 'N/A'}%`} size="small" variant="outlined" />
                                    <Chip label={`BP: ${status.current_vitals.bp_sys || 'N/A'}/${status.current_vitals.bp_dia || 'N/A'}`} size="small" variant="outlined" />
                                  </Box>
                                </>
                              )}
                            </>
                          )}
                        </Box>
                      </CardContent>
                      
                      <CardActions sx={{ justifyContent: 'center', p: 1.5, pt: 0 }}>
                        {isRunning ? (
                          <Button
                            variant="contained"
                            color="error"
                            startIcon={<StopIcon />}
                            onClick={() => handleStopSimulation(device.id)}
                            size="small"
                            fullWidth
                          >
                            Stop Simulation
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<StartIcon />}
                            onClick={() => handleStartSimulation(device.id)}
                            size="small"
                            fullWidth
                          >
                            Start Simulation
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      ) : (
        /* Table View */
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Device Status
            </Typography>
            <TableContainer 
            component={Paper} 
            variant="outlined"
            sx={{ overflowX: 'auto' }} // Enable horizontal scroll on mobile
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Device Name</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Serial Number</TableCell>
                  <TableCell>ECG Profile</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Uptime</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Packets Sent</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Current Vitals</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="textSecondary">
                        No devices found. Go to Devices tab to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  devices.map((device) => {
                    const status = getDeviceStatus(device.id);
                    const isRunning = status.status === 'running';
                    
                    // Format ECG profile name for display
                    const formatECGProfile = (profile) => {
                      if (!profile) return 'Normal';
                      const profileLabels = {
                        'normal': 'Normal Sinus',
                        'real_dgt7_normal': 'Real DGT7 Normal',
                        'neonate': 'Neonate',
                        'tachycardia': 'Tachycardia',
                        'bradycardia': 'Bradycardia',
                        'afib': 'AFib',
                        'ventricular_tachycardia': 'V-Tach',
                        'ventricular_fibrillation': 'V-Fib',
                        'atrial_flutter': 'A-Flutter',
                        'first_degree_heart_block': '1° Block',
                        'second_degree_heart_block': '2° Block',
                        'third_degree_heart_block': '3° Block',
                        'stemi': 'STEMI',
                        'stroke': 'Stroke'
                      };
                      return profileLabels[profile] || profile;
                    };
                    
                    // Get profile color
                    const getProfileColor = (profile) => {
                      const criticalProfiles = ['stemi', 'stroke', 'ventricular_tachycardia', 'ventricular_fibrillation', 'third_degree_heart_block'];
                      const warningProfiles = ['tachycardia', 'bradycardia', 'afib', 'second_degree_heart_block'];
                      
                      if (criticalProfiles.includes(profile)) return 'error';
                      if (warningProfiles.includes(profile)) return 'warning';
                      return 'default';
                    };
                    
                    return (
                      <TableRow key={device.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {device.name}
                          </Typography>
                          {/* Show serial on mobile below name */}
                          <Typography 
                            variant="caption" 
                            sx={{ display: { xs: 'block', md: 'none' }, color: 'text.secondary' }}
                          >
                            {device.serial_number}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <code>{device.serial_number}</code>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatECGProfile(device.ecg_profile)}
                            color={getProfileColor(device.ecg_profile)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={status.status || device.status}
                            color={isRunning ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          {formatUptime(status.uptime_seconds)}
                        </TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          {(status.packets_sent || 0).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {status.current_vitals && (
                            <Box>
                              <Typography variant="caption" display="block">
                                HR: {status.current_vitals.hr || 'N/A'} bpm
                              </Typography>
                              <Typography variant="caption" display="block">
                                SpO2: {status.current_vitals.spo2 || 'N/A'}%
                              </Typography>
                              <Typography variant="caption" display="block">
                                BP: {status.current_vitals.bp_sys || 'N/A'}/{status.current_vitals.bp_dia || 'N/A'}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {isRunning ? (
                            <Tooltip title="Stop Simulation">
                              <IconButton
                                color="error"
                                onClick={() => handleStopSimulation(device.id)}
                                size="small"
                              >
                                <StopIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Start Simulation">
                              <IconButton
                                color="success"
                                onClick={() => handleStartSimulation(device.id)}
                                size="small"
                              >
                                <StartIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      )}
    </Box>
  );
};

export default Dashboard;


